import { Chip, Tooltip, useTheme } from '@mui/material';
import { green } from '@mui/material/colors';
import { AthletePreview, BlockPrintSettings, Tag } from '@strength/domain';
import { isLayerEmpty } from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { isValidUUID } from '@strength/utils';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { MdCheck, MdInfo } from '@teamworksdev/icons';
import { inArray } from '@teamworksdev/loco-sync-client';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryOne } from '../../../loco-sync';
import { trpc } from '../../../trpc/react';
import { Page } from '../../components/layout/page';
import { PageHeader } from '../../components/layout/page-header';
import { useViewer } from '../../context/viewer';
import { useBootstrap } from '../../hooks/useBootstrap';
import { useMetrics } from '../../hooks/useMetrics';
import { useStorage } from '../../hooks/useStorage';
import { useToast } from '../../hooks/useToast';
import { FocusOption } from '../library/schemes/types';
import { LoadingPage } from '../loading';
import { BlockBuilder } from './block-builder/BlockBuilder';
import {
  BlockGroupByState,
  DEFAULT_BLOCK_GROUP_BY_STATE,
  deriveGroupBy,
  normalizeBlockGroupByState,
  toggleGroupBy,
} from './block-group-by';
import { BlockSummary } from './block-summary/BlockSummary';
import { DisplayedWorkoutBadge } from './export/DisplayedWorkoutBadge';
import { ExportDrawer } from './export/ExportDrawer';
import { ExportErrorBoundary } from './export/ExportErrorBoundary';
import { ExportHeader } from './export/ExportHeader';
import { PublishChip, PublishHeader } from './publish/header';
import { usePublishState } from './publish/publish-state';
import {
  AthleteActualWorkoutLayer,
  AthleteModifiedPublishedWorkoutLayer,
  BaseBlockSheetProps,
  BlockDraftWorkoutLayer,
  BlockGroupBy,
  BlockPreview,
  BlockPublishedWorkoutLayer,
} from './types';
import {
  blockDraftToPreview,
  hasBlockBeenPublished as computeHasBlockBeenPublished,
} from './utils';

const routeApi = getRouteApi('/orgAuth/orgWithNav/$teamId/block/$blockId');

type BlockSearch = {
  athleteId?: string;
  workoutId?: string;
  showActuals?: boolean;
  crossTeamId?: string;
};

type BlockPageView = 'builder' | 'summary';

export function BlockPage() {
  const { t } = useTranslation();
  const { blockId, teamId } = routeApi.useParams();
  const { crossTeamId } = routeApi.useSearch<BlockSearch>();
  const { error: metricsError } = useMetrics();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { team } = useViewer();

  // Cross-team blocks carry the block's actual teamId as crossTeamId. Only
  // show read-only if the coach isn't a member of that team.
  const readOnly =
    !!crossTeamId && !team.teams.some((t) => t.id === crossTeamId);

  const syncGroups = useMemo(
    () =>
      readOnly
        ? [{ type: 'CrossTeamBlock_Readonly' as const, blockId }]
        : [{ type: 'TeamCoach_BlockBuilder' as const, blockId }],
    [blockId, readOnly],
  );
  const { isLoading } = useBootstrap(syncGroups);

  const { data: localBlock, isHydrated: localBlockIsHydrated } = useQueryOne(
    'Block',
    { id: blockId },
  );

  const blockQuery = trpc.block.blockForDraft.useQuery(
    { blockId },
    {
      enabled: isValidUUID(blockId),
      keepPreviousData: true,
    },
  );

  // Bootstrap finishing (`isLoading` → false) doesn't guarantee `useQueryOne`
  // has settled — on a fresh tab the local query can return `undefined` for
  // one render after bootstrap completes. Guard the "gone" decision on the
  // local query's own hydration signal so deep-links don't bounce during
  // that gap.
  const blockIsGone =
    !readOnly &&
    !isLoading &&
    localBlockIsHydrated &&
    (!localBlock || localBlock.deletedAt != null);

  // The block URL outlives the block: the user may navigate to a block that
  // was deleted in another tab/session, or have the page open when someone
  // else deletes it. In either case the local cache is authoritative —
  // bounce to the planner with a toast rather than leave them on a broken
  // page or pelt the server with 404s.
  useEffect(() => {
    if (!blockIsGone) return;
    toast({
      severity: 'warning',
      title: t('blockNotFound'),
    });
    navigate({
      to: '/$teamId/plan/timeline',
      params: { teamId },
      replace: true,
    });
  }, [blockIsGone, navigate, teamId, toast, t]);

  const blockPrintSettingsQuery = trpc.teamSettings.blockPrintSettings.useQuery(
    { blockId },
    {
      staleTime: 10_000,
      enabled: isValidUUID(blockId),
    },
  );

  const editBlock = trpc.block.editBlock.useMutation({
    onSuccess: () => {
      blockQuery.refetch();
    },
  });

  const onBlockLayoutChange = (cycleSortOrder: number, sessions: number) => {
    const block = blockQuery.data?.block;
    if (!block) {
      return;
    }

    const updatedLayout = {
      ...block.layout,
      [cycleSortOrder]: sessions,
    };

    editBlock.mutate({
      ...block,
      focuses: block.focuses.map((f, index) => ({
        ...f,
        sortOrder: index,
      })),
      layout: updatedLayout,
    });
  };

  const { data: blockWorkoutLayers, isHydrated } = useQuery(
    'BlockDraftWorkoutLayer',
    {
      blockId,
      deletedAt: null,
    },
  );

  const { data: blockPublishedWorkoutLayer } = useQuery(
    'BlockPublishedWorkoutLayer',
    {
      blockId,
      // dont filter for current layers bc in progress athlete layers
      // might need to reference not current published block layers
    },
  );

  const { data: actualWorkoutLayers } = useQuery('AthleteActualWorkoutLayer', {
    blockId,
  });

  const { data: modifiedPublishedWorkoutLayers } = useQuery(
    'AthleteModifiedPublishedWorkoutLayer',
    {
      blockId,
    },
  );

  // Need special treatment for isHydrated because new BlockDraftWorkoutLayer can be created
  // Which technically means the query is not hydrated because the associated workout layer data would not been loaded from storage
  // However, there won't actually be any new workout layer data in storage because the block is brand new
  // Can remove this once workout layer data is one object
  const shouldRenderRef = useRef(false);
  if (isHydrated) {
    shouldRenderRef.current = true;
  }

  const focusesQuery = trpc.schemeLibrary.trainingFocuses.useQuery(undefined, {
    staleTime: 10_000,
  });
  const focusOptions = focusesQuery.data || [];

  const workoutTemplateTagsQuery = trpc.tag.tags.useQuery({
    types: ['workout_template'],
  });
  const workoutTemplateTagOptions = workoutTemplateTagsQuery.data || [];

  let content: ReactNode;
  if (
    !isLoading &&
    blockQuery.data &&
    blockPrintSettingsQuery.data &&
    shouldRenderRef.current
  ) {
    content = (
      <BlockContent
        block={blockDraftToPreview(blockQuery.data.block)}
        blockWorkoutLayers={blockWorkoutLayers}
        actualWorkoutLayers={actualWorkoutLayers}
        blockPublishedWorkoutLayers={blockPublishedWorkoutLayer}
        modifiedPublishedWorkoutLayers={modifiedPublishedWorkoutLayers}
        athletes={blockQuery.data.athletes}
        focusOptions={focusOptions}
        workoutTemplateTagOptions={workoutTemplateTagOptions}
        blockPrintSettings={blockPrintSettingsQuery.data}
        t={t}
        onBlockLayoutChange={onBlockLayoutChange}
        readOnly={readOnly}
      />
    );
  } else if (
    blockQuery.error ||
    metricsError ||
    blockPrintSettingsQuery.error
  ) {
    content = (
      <div className="flex items-center justify-center h-full text-2xl">
        {t('unableToLoadBlock')}
      </div>
    );
  } else {
    content = <LoadingPage />;
  }

  return <Page>{content}</Page>;
}

function BlockContent({
  block,
  blockWorkoutLayers,
  blockPublishedWorkoutLayers,
  actualWorkoutLayers,
  modifiedPublishedWorkoutLayers,
  athletes,
  focusOptions,
  workoutTemplateTagOptions,
  blockPrintSettings,
  t,
  onBlockLayoutChange,
  readOnly,
}: {
  block: BlockPreview;
  blockWorkoutLayers: BlockDraftWorkoutLayer[];
  blockPublishedWorkoutLayers: BlockPublishedWorkoutLayer[];
  actualWorkoutLayers: AthleteActualWorkoutLayer[];
  modifiedPublishedWorkoutLayers: AthleteModifiedPublishedWorkoutLayer[];
  athletes: AthletePreview[];
  focusOptions: FocusOption[];
  workoutTemplateTagOptions: Tag[];
  blockPrintSettings: BlockPrintSettings;
  t: I18nTranslator;
  onBlockLayoutChange: (cycleSortOrder: number, sessions: number) => void;
  readOnly?: boolean;
}) {
  const {
    team,
    org: { hasDigitalDisplays },
  } = useViewer();
  const navigate = useNavigate();
  const view = block.blockType === 'bowling' ? 'summary' : 'builder';

  // Shared by the Export trigger and the DisplayedWorkoutBadge - both open
  // this same drawer instance, just to different starting tabs.
  const [isExportDrawerOpen, setIsExportDrawerOpen] = useState(false);
  const [exportDrawerInitialTab, setExportDrawerInitialTab] = useState<
    'print' | 'digitalDisplay'
  >('print');
  const openExportDrawer = (tab: 'print' | 'digitalDisplay') => {
    setExportDrawerInitialTab(tab);
    setIsExportDrawerOpen(true);
  };
  // "Stop Displaying" unmounts DisplayedWorkoutBadge, so when the drawer was
  // opened from the badge there is nothing left for the modal to hand focus
  // back to. Fall back to the Export trigger, which always survives.
  const exportTriggerRef = useRef<HTMLButtonElement>(null);

  // if refreshing on the block we need to set the selected team to the block team.
  // Skip for readonly cross-team views — setting the selected team to a cross-team
  // ID would cause route guard redirects on subsequent navigations.
  useEffect(() => {
    if (readOnly) return;
    if (block.teamId && block.teamId !== team.selectedTeam) {
      team.setSelectedTeam(block.teamId);
    }
  }, [block.teamId, readOnly, team]);

  // this is only used when there is a single athlete
  const { data: athleteWorkoutLayers = [] } = useQuery(
    'AthleteDraftWorkoutLayer',
    {
      blockId: block.id,
      profileId: inArray(athletes.map((a) => a.id)),
    },
  );

  const useSingleAthleteMode =
    athletes.length === 1 &&
    // can't use single athlete mode if there is content in the athlete layer
    // of any workout due to not being able to show it
    athleteWorkoutLayers.every(isLayerEmpty);

  const { athleteId, workoutId, showActuals } =
    routeApi.useSearch<BlockSearch>();
  const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(
    // In readonly mode skip the useSingleAthleteMode guard — no writes happen,
    // so we always want to show the athlete's full merged prescription.
    // always show AthleteSheet in readonly
    readOnly
      ? (athleteId ?? null)
      : athleteId && !useSingleAthleteMode
        ? athleteId
        : null,
  );

  // Keep selectedAthleteId in sync when the URL athleteId changes in readonly
  // mode (e.g. navigating between athletes without a full remount).
  // Non-readonly navigation goes through the block builder's own athlete picker
  // and always remounts this component, so the useState initializer handles it.
  useEffect(() => {
    if (readOnly) {
      setSelectedAthleteId(athleteId ?? null);
    }
  }, [readOnly, athleteId]);

  const { data: athletePublishedLayers } = useQuery(
    'AthletePublishedWorkoutLayer',
    {
      blockId: block.id,
      isCurrent: true,
    },
  );

  const [startingCycle, startingSession] = useMemo(() => {
    if (!workoutId) return [1, 1];
    // Prefer the athlete-published layer (bulk-actuals deep-link path).
    const fromAthletePublished = athletePublishedLayers?.find(
      (l) => l.workoutId === workoutId,
    );
    if (fromAthletePublished) {
      return [
        fromAthletePublished.cycleSortOrder,
        fromAthletePublished.sessionSortOrder,
      ];
    }
    // Fall back to the draft layer — this exists for every workout in the
    // block, so it covers the All-view card-click path where workouts are
    // not yet published.
    const fromDraft = blockWorkoutLayers.find((l) => l.workoutId === workoutId);
    if (fromDraft) {
      return [fromDraft.cycleSortOrder, fromDraft.sessionSortOrder];
    }
    return [1, 1];
  }, [athletePublishedLayers, blockWorkoutLayers, workoutId]);

  const [storedGroupBy, setStoredGroupBy] = useStorage<BlockGroupByState>(
    'blockGroupBy',
    DEFAULT_BLOCK_GROUP_BY_STATE,
  );
  const groupByState = normalizeBlockGroupByState(storedGroupBy);
  const setGroupBy = (selected: BlockGroupBy) => {
    setStoredGroupBy((prev) => toggleGroupBy(prev, selected));
    // Clear workoutId from the URL so the user can navigate freely after
    // arriving via a deep-link (otherwise deriveGroupBy keeps returning
    // state.view while workoutId is present).
    if (workoutId) {
      navigate({
        to: '.',
        search: (prev: BlockSearch) => ({
          ...prev,
          workoutId: undefined,
        }),
        replace: true,
      });
    }
  };
  const groupBy: BlockGroupBy = deriveGroupBy({
    state: groupByState,
    hasWorkoutId: !!workoutId,
  });
  const [sessionId, setSessionId] = useState(() => startingSession);
  const [cycleId, setCycleId] = useState(() => startingCycle);

  // athletePublishedLayers hydrates asynchronously; the useState initializers
  // above only run at mount, so any non-default position computed after
  // hydration must be pushed into state explicitly.
  useEffect(() => {
    if (startingCycle !== 1 || startingSession !== 1) {
      setCycleId(startingCycle);
      setSessionId(startingSession);
    }
  }, [startingCycle, startingSession]);

  const { data: publishTask } = useQueryOne('BlockPublishTask', {
    blockId: block.id,
  });

  // Combine create a map of athletes to a boolean of if any are failed
  const athleteTaskMap = useMemo(() => {
    if (!publishTask || !publishTask.failedWorkouts) {
      return {} as Record<string, boolean>;
    }

    return Object.values(publishTask.failedWorkouts).reduce(
      (acc, ids) => {
        ids.forEach((id) => {
          acc[id] = true;
        });
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [publishTask]);

  const hasBlockBeenPublished = computeHasBlockBeenPublished(
    blockPublishedWorkoutLayers,
    athletePublishedLayers,
  );
  const publishState = usePublishState(block, hasBlockBeenPublished);

  const hasUnpublishedChanges = useMemo(() => {
    const blockHasChanges = blockWorkoutLayers.some(
      (l) =>
        ((l as { unpublishedChangeCount?: number }).unpublishedChangeCount ??
          0) > 0,
    );
    const athleteHasChanges = athleteWorkoutLayers.some(
      (l) => l.unpublishedChangeCount > 0,
    );
    return blockHasChanges || athleteHasChanges;
  }, [blockWorkoutLayers, athleteWorkoutLayers]);

  const isBlockCanceled = block.status === 'canceled';

  const builderProps: Omit<BaseBlockSheetProps, 'view'> = {
    block,
    hasBlockBeenPublished,
    publishState,
    blockWorkoutLayers,
    actualWorkoutLayers,
    blockPublishedWorkoutLayers,
    athletePublishedWorkoutLayers: athletePublishedLayers,
    modifiedPublishedWorkoutLayers,
    focusOptions,
    workoutTemplateTagOptions,
    readOnly,
  };

  return (
    <>
      <PageHeader
        title={block.name}
        rightSlot={
          <div className="flex flex-row gap-2 items-center">
            {!readOnly && view === 'summary' && (
              <ChangeStatusBadge
                hasChanges={hasUnpublishedChanges}
                hasBlockBeenPublished={hasBlockBeenPublished}
              />
            )}
            {!readOnly &&
              !isBlockCanceled &&
              view === 'builder' &&
              hasBlockBeenPublished && <MdCheck size={24} color={green[400]} />}
            {isBlockCanceled && <CanceledBlockBadge />}
            {!readOnly && (
              <PublishChip
                state={publishState}
                groupBy={groupBy}
                setSessionId={setSessionId}
                setCycleId={setCycleId}
              />
            )}
            {!readOnly && view === 'builder' && hasDigitalDisplays && (
              <DisplayedWorkoutBadge
                block={block}
                blockPublishedWorkoutLayers={blockPublishedWorkoutLayers}
                onClick={() => openExportDrawer('digitalDisplay')}
              />
            )}
            {!readOnly && view === 'builder' && (
              <ExportHeader
                onClick={() => openExportDrawer('print')}
                buttonRef={exportTriggerRef}
              />
            )}
            {!readOnly && (
              <PublishHeader
                blockId={block.id}
                blockName={block.name}
                state={publishState}
                hasAthleteWorkouts={athletes.length > 0}
              />
            )}
          </div>
        }
      />

      {!readOnly && view === 'builder' && isExportDrawerOpen && (
        <ExportErrorBoundary>
          <ExportDrawer
            open={isExportDrawerOpen}
            onOpenChange={setIsExportDrawerOpen}
            onDisplayStopped={() => exportTriggerRef.current?.focus()}
            initialTab={exportDrawerInitialTab}
            athletes={athletes}
            block={block}
            blockWorkoutLayers={blockWorkoutLayers}
            blockPublishedWorkoutLayers={blockPublishedWorkoutLayers}
            blockPrintSettings={blockPrintSettings}
            initAthleteId={selectedAthleteId}
            initCycleId={cycleId}
            initSessionId={sessionId}
          />
        </ExportErrorBoundary>
      )}

      {view === 'builder' && (
        <BlockBuilder
          {...builderProps}
          groupBy={groupBy}
          sessionId={sessionId}
          cycleId={cycleId}
          setSessionId={setSessionId}
          setCycleId={setCycleId}
          athletes={athletes}
          selectedAthleteId={selectedAthleteId}
          setSelectedAthleteId={setSelectedAthleteId}
          useSingleAthleteMode={useSingleAthleteMode}
          athleteTaskMap={athleteTaskMap}
          setGroupBy={setGroupBy}
          onBlockLayoutChange={onBlockLayoutChange}
          selectedWorkoutId={workoutId}
          initShowActuals={!!showActuals}
        />
      )}
      {view === 'summary' && (
        <BlockSummary
          {...builderProps}
          athletes={athletes}
          athleteDraftWorkoutLayers={athleteWorkoutLayers}
          selectedAthleteId={selectedAthleteId}
          setSelectedAthleteId={setSelectedAthleteId}
        />
      )}
    </>
  );
}

const CanceledBlockBadge = () => {
  const {
    palette: { info },
  } = useTheme();
  const { t } = useTranslation();
  return (
    <Tooltip placement="bottom" title={t('canceledBlockTooltip')} arrow>
      <div className="flex items-center gap-2 bg-informational-25 p-2 px-4 rounded-md cursor-default">
        <MdInfo size={24} color={info.main} />
        <span className="text-sm font-semibold">{t('blockCanceled')}</span>
      </div>
    </Tooltip>
  );
};

const ChangeStatusBadge = ({
  hasChanges,
  hasBlockBeenPublished,
}: {
  hasChanges: boolean;
  hasBlockBeenPublished: boolean;
}) => {
  const { t } = useTranslation();
  const isPublished = hasBlockBeenPublished && !hasChanges;
  return (
    <Chip
      sx={{
        height: 25,
        '.MuiChip-label': {
          px: 1,
          fontSize: 11,
        },
      }}
      className={`rounded-full text-xs ${
        isPublished
          ? 'bg-green-100/50 text-success-700 font-medium'
          : 'bg-light-100/50 text-light-800 font-medium'
      }`}
      label={isPublished ? t('allChangesPublished') : t('unpublishedChanges')}
    />
  );
};
